/* eslint-disable indent */
import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Avatar } from '@/components/Avatar';
import { Input } from '@/components/Controls/Input';
import { Switch } from '@/components/Controls/Switch';
import { FormField } from '@/components/Form/FormField';
import { useEffect, useState } from 'react';
import { FieldValues, Path, PathValue } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { Select } from '@/components/Controls/Select';
import { WarningTag } from '@/components/Tag/WarningTag';
import { FormManagementProps } from '@/type/form.types';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { useGetExistingUser } from '@/hooks/user.hooks';
import { allFieldErrorsEmpty, resetFieldError } from '@/utils/object.utils';
import { FieldError } from '@/type/error.types';
import { RoleName } from '@/api/entities/user-role-type';

interface UserProfileManagementProps<
  TSchema extends FieldValues,
> extends FormManagementProps<TSchema> {
  roleType?: RoleName;
}

export const UserProfileManagement = <TSchema extends FieldValues>({
  t,
  roleType,
  data,
  watch,
  register,
  setValue,
  setError,
  errors,
  submitDisabled,
  onSubmit,
}: UserProfileManagementProps<TSchema>) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  /**
   * === Set Value Watchers section ===
   */
  const emailValue = watch && watch('email' as Path<TSchema>);

  const activeValue = watch && watch('active' as Path<TSchema>);
  /**
   * === END OF Set Value Watchers section ===
   */

  const { data: existingUser } = useGetExistingUser({
    id: data?.id,
    email: emailValue,
  });

  /**
   * === Reset Error Messages On Value Change section ===
   */
  useEffect(() => {
    if (emailValue) {
      resetFieldError<TSchema>({ fieldName: 'email', setError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailValue]);
  /**
   * === END OF Reset Error Messages On Value Change section ===
   */

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && data) {
      setValue('profilePicture' as Path<TSchema>, data?.profilePicture ?? '');
      setValue('title' as Path<TSchema>, data?.title ?? '');
      setValue('lastname' as Path<TSchema>, data?.lastname ?? '');
      setValue('firstname' as Path<TSchema>, data?.firstname ?? '');
      setValue('phone' as Path<TSchema>, data?.phone ?? '');
      setValue('email' as Path<TSchema>, data?.email ?? '');
      setValue('password' as Path<TSchema>, data?.password ?? '');
      setValue('active' as Path<TSchema>, data?.active);
    }
  }, [setValue, data]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  /**
   * === Handle Submit section ===
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const userErrorMessages = existingUser?.data?.errorMessages;

    if (userErrorMessages?.includes(FieldError.EMAIL_EXISTS)) {
      setError('email' as Path<TSchema>, {
        message: t('common:userEmailAlreadyExists'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (allFieldErrorsEmpty(errors)) {
      onSubmit?.();
    }
  };
  /**
   * === END OF Handle Submit section ===
   */

  return (
    <form onSubmit={handleSubmit}>
      <Card
        title={
          <TitleSection
            text={t('userProfile')}
            defaultIconClassName="ki-badge"
          />
        }
        footer={
          <Button
            buttonClassName="px-8"
            text={
              submitDisabled ? `${t('common:loading')}...` : t('common:save')
            }
            disabled={submitDisabled}
          />
        }
        bodyClassName="py-8 flex flex-col gap-5"
      >
        <FormField
          text={t('common:avatar')}
          containerClassName="mb-2 max-md:mb-0"
          textWrapperClassName="mt-3 max-md:mt-0"
        >
          <Avatar
            outerContainerClassName="max-md:w-full"
            containerClassName="max-md:mx-auto"
            imageUrl={data?.profilePicture}
            onChange={(url) =>
              setValue?.(
                'profilePicture' as Path<TSchema>,
                url as PathValue<TSchema, Path<TSchema>>,
              )
            }
          />
        </FormField>
        <FormField
          text={t('common:title')}
          required
          textWrapperClassName="mt-3"
        >
          <div className="flex flex-1 flex-wrap items-start gap-5">
            <Select
              {...(register && register('title' as Path<TSchema>))}
              errorMessage={errors && (errors.title?.message as string)}
              placeholder={t('common:selectTitle')}
              options={[
                {
                  label: t('common:mister'),
                  value: 'mister',
                },
                {
                  label: t('common:miss'),
                  value: 'miss',
                },
              ]}
            />
          </div>
        </FormField>
        <FormField
          text={t('common:username')}
          required
          textWrapperClassName="mt-3"
        >
          <div className="flex flex-1 flex-wrap items-start gap-5">
            <Input
              {...(register && register('lastname' as Path<TSchema>))}
              errorMessage={errors && (errors.lastname?.message as string)}
              placeholder={t('common:lastname')}
              type="text"
              containerClassName="max-1.5xl:flex-none max-1.5xl:w-full"
            />
            <Input
              {...(register && register('firstname' as Path<TSchema>))}
              errorMessage={errors && (errors.firstname?.message as string)}
              placeholder={t('common:firstname')}
              type="text"
              containerClassName="max-1.5xl:flex-none max-1.5xl:w-full"
            />
          </div>
        </FormField>
        <FormField
          text={t('common:email')}
          required
          textWrapperClassName="mt-3"
        >
          <div className="flex flex-1 flex-wrap items-start gap-5">
            <Input
              {...(register && register('email' as Path<TSchema>))}
              errorMessage={errors && (errors.email?.message as string)}
              placeholder={t('common:email')}
              type="email"
            />
          </div>
        </FormField>
        {!data && (
          <FormField
            text={t('common:password')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-col gap-4">
              <WarningTag
                text={t('common:ensurePasswordIsCommunicatedToTheUser')}
                icon={
                  <i className="ki-solid ki-information-2 text-blue-4982FC text-xl" />
                }
              />
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register && register('password' as Path<TSchema>))}
                  errorMessage={errors && (errors.password?.message as string)}
                  autoComplete="new-password"
                  placeholder={t('common:password')}
                  type={showPassword ? 'text' : 'password'}
                  rightIcon={
                    <i
                      className={twMerge(
                        'ki-filled text-gray-500 text-xl hover:cursor-pointer',
                        showPassword ? 'ki-eye-slash' : 'ki-eye',
                      )}
                      onClick={() => setShowPassword((prevState) => !prevState)}
                    />
                  }
                  containerClassName="max-1.5xl:flex-none max-1.5xl:w-full"
                  inputClassName="h-10"
                />
                <Input
                  {...(register &&
                    register('passwordConfirm' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.passwordConfirm?.message as string)
                  }
                  autoComplete="new-password"
                  placeholder={t('common:reEnterPassword')}
                  type={showPasswordConfirm ? 'text' : 'password'}
                  rightIcon={
                    <i
                      className={twMerge(
                        'ki-filled text-gray-500 text-xl hover:cursor-pointer',
                        showPasswordConfirm ? 'ki-eye-slash' : 'ki-eye',
                      )}
                      onClick={() =>
                        setShowPasswordConfirm((prevState) => !prevState)
                      }
                    />
                  }
                  containerClassName="max-1.5xl:flex-none max-1.5xl:w-full"
                  inputClassName="h-10"
                />
              </div>
            </div>
          </FormField>
        )}
        <FormField
          text={t('common:phoneNumber')}
          required
          textWrapperClassName="mt-3"
        >
          <div className="flex flex-1 flex-wrap items-start gap-5">
            <Input
              {...(register && register('phone' as Path<TSchema>))}
              errorMessage={errors && (errors.phone?.message as string)}
              placeholder={t('common:phoneNumber')}
              type="tel"
            />
          </div>
        </FormField>
        {roleType &&
          roleType !== RoleName.MODERATOR &&
          roleType !== RoleName.BRANCH_AGENT && (
            <FormField
              text={t('common:thisUserCanLogin')}
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
                <Switch
                  {...(register && register('active' as Path<TSchema>))}
                  errorMessage={errors && (errors.active?.message as string)}
                  checked={data ? activeValue : true}
                />
              </div>
            </FormField>
          )}
      </Card>
    </form>
  );
};
